import type { TableRow, TableColumn, AggregationType } from '@/app/types/table';

/**
 * Generate a totals row for the table
 */
export function generateTotalsRow(
  rows: TableRow[],
  columns: TableColumn[],
  aggregations: { [columnId: string]: AggregationType },
  label: string = 'Summe'
): TableRow {
  // Filter out group headers, subtotals, and grand totals
  const dataRows = rows.filter(
    row => !row.isGroupHeader && !row.isSubtotal && !row.isGrandTotal
  );

  // Calculate aggregations
  const calculatedAggregations = calculateAllAggregations(dataRows, aggregations, columns);

  // Create the row data
  const rowData: { [columnId: string]: any } = {};
  
  columns.forEach((col, idx) => {
    if (idx === 0) {
      // First column shows the label
      rowData[col.id] = {
        id: `totals-${col.id}`,
        rowIndex: -1,
        colIndex: idx,
        value: label,
        dataType: 'string' as const,
        style: {
          fontWeight: 'bold' as const,
          backgroundColor: 'var(--md-surface-container-high)',
        },
      };
    } else {
      // Other columns show aggregation or empty
      const aggValue = calculatedAggregations[col.id];
      rowData[col.id] = {
        id: `totals-${col.id}`,
        rowIndex: -1,
        colIndex: idx,
        value: aggValue ?? '',
        dataType: col.dataType,
        style: {
          fontWeight: 'bold' as const,
          backgroundColor: 'var(--md-surface-container-high)',
        },
      };
    }
  });

  return {
    id: 'totals-row',
    index: -1,
    height: 40,
    isGrandTotal: true,
    data: rowData,
    aggregations: calculatedAggregations,
  };
}

/**
 * Calculate aggregation for a set of rows and a specific column
 */
export function calculateAggregation(
  rows: TableRow[],
  columnId: string,
  aggregationType: AggregationType,
  columns: TableColumn[]
): number | string {
  if (aggregationType === 'none' || rows.length === 0) {
    return '';
  }

  // Extract values from rows for this column
  const values = rows
    .map(row => row.data[columnId]?.value)
    .filter(val => val !== undefined && val !== null && val !== '');

  if (values.length === 0) {
    return aggregationType === 'count' ? 0 : '';
  }

  switch (aggregationType) {
    case 'count':
      return values.length;

    case 'sum': {
      const numericValues = values
        .map(val => typeof val === 'number' ? val : parseFloat(String(val)))
        .filter(val => !isNaN(val));
      
      if (numericValues.length === 0) return '';
      return numericValues.reduce((sum, val) => sum + val, 0);
    }

    case 'avg': {
      const numericValues = values
        .map(val => typeof val === 'number' ? val : parseFloat(String(val)))
        .filter(val => !isNaN(val));
      
      if (numericValues.length === 0) return '';
      const sum = numericValues.reduce((sum, val) => sum + val, 0);
      return sum / numericValues.length;
    }

    case 'min': {
      const numericValues = values
        .map(val => typeof val === 'number' ? val : parseFloat(String(val)))
        .filter(val => !isNaN(val));
      
      if (numericValues.length === 0) return '';
      return Math.min(...numericValues);
    }

    case 'max': {
      const numericValues = values
        .map(val => typeof val === 'number' ? val : parseFloat(String(val)))
        .filter(val => !isNaN(val));
      
      if (numericValues.length === 0) return '';
      return Math.max(...numericValues);
    }

    case 'first':
      return values[0];

    case 'last':
      return values[values.length - 1];

    case 'unique_count': {
      const uniqueValues = new Set(values.map(v => String(v)));
      return uniqueValues.size;
    }

    default:
      return '';
  }
}

/**
 * Calculate all aggregations for a set of rows
 */
export function calculateAllAggregations(
  rows: TableRow[],
  aggregations: { [columnId: string]: AggregationType },
  columns: TableColumn[]
): { [columnId: string]: number | string } {
  const result: { [columnId: string]: number | string } = {};

  for (const [columnId, aggregationType] of Object.entries(aggregations)) {
    if (aggregationType !== 'none') {
      result[columnId] = calculateAggregation(rows, columnId, aggregationType, columns);
    }
  }

  return result;
}

/**
 * Format aggregation value for display
 */
export function formatAggregation(
  value: number | string,
  aggregationType: AggregationType,
  columnDataType: 'string' | 'number' | 'boolean' | 'attribute' | 'formula'
): string {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    // Format numbers based on aggregation type
    if (aggregationType === 'count' || aggregationType === 'unique_count') {
      return value.toString();
    }
    
    if (aggregationType === 'avg') {
      return value.toFixed(2);
    }

    // For sum, min, max - check if it's a decimal
    if (Number.isInteger(value)) {
      return value.toString();
    } else {
      return value.toFixed(2);
    }
  }

  return String(value);
}

/**
 * Get aggregation label for display
 */
export function getAggregationLabel(aggregationType: AggregationType): string {
  switch (aggregationType) {
    case 'sum': return 'Summe';
    case 'avg': return 'Durchschnitt';
    case 'count': return 'Anzahl';
    case 'min': return 'Minimum';
    case 'max': return 'Maximum';
    case 'first': return 'Erster';
    case 'last': return 'Letzter';
    case 'unique_count': return 'Eindeutige';
    case 'none': return 'Keine';
    default: return '';
  }
}

/**
 * Check if a column can use a specific aggregation type
 */
export function isAggregationCompatible(
  columnDataType: 'string' | 'number' | 'boolean' | 'attribute' | 'formula',
  aggregationType: AggregationType
): boolean {
  // Count and unique_count work on all types
  if (aggregationType === 'count' || aggregationType === 'unique_count' || aggregationType === 'none') {
    return true;
  }

  // first and last work on all types
  if (aggregationType === 'first' || aggregationType === 'last') {
    return true;
  }

  // sum, avg, min, max work on numbers, formulas, AND strings (which may contain numeric values)
  if (aggregationType === 'sum' || aggregationType === 'avg' || aggregationType === 'min' || aggregationType === 'max') {
    return columnDataType === 'number' || columnDataType === 'formula' || columnDataType === 'string';
  }

  return false;
}